import { JSXElementConstructor, Key, ReactElement, ReactNode, ReactPortal, useEffect, useState } from 'react';
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, CircularProgress, IconButton, TextField, MenuItem, FormControlLabel, Checkbox, Tooltip } from "@mui/material";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import { doc, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db, SECRETS_ENV } from "../../../Config/firebase.ts";
import { toast } from 'react-toastify';
import { getFutureCycles, logAction } from "../utilityFunctions.ts";

import { useBackend } from "../../../hooks/backend/useBackend.ts";
import InvoiceResultsDrawer from './InvoiceResultsDrawer';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { ActionType } from '../../../enums.ts';


const secretCloudTaskToken = import.meta.env.VITE_SECRET_CLOUD_TASK_TOKEN || process.env.VITE_SECRET_CLOUD_TASK_TOKEN

interface FormData {
    cycle: string;
    selectedSubscriptions: string[];
}

interface InvoiceResult {
    sentCount: number;
    failedDetails: {
        studentName: string;
        subscriptionName: string;
        reason: string;
    }[];
}

export default function BulkRaiseInvoiceModal() {
    // Modal Controllers
    const dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.BulkRaiseInvoiceModalOpen);
    const serviceProviderId = useAppSelector(selectEffectiveUID);
    const subscriptions = useAppSelector((state) => state.subscriptionTableSlice.data);
    const { uid: spId, display_name: spName } = useAppSelector((state: RootState) => state.userSliceV2?.data) || {}
    // Component States
    const [formData, setFormData] = useState<FormData>({
        cycle: "",
        selectedSubscriptions: []
    });

    const [isLoading, setIsLoading] = useState(false);
    const [availableCycles, setAvailableCycles] = useState<string[]>([]);
    const { axiosPostRequest } = useBackend("");
    const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(true);
    const [invoiceResults, setInvoiceResults] = useState<InvoiceResult | null>(null);
    const [showResults, setShowResults] = useState(false);
    const [processingSubscriptions, setProcessingSubscriptions] = useState<Set<string>>(new Set());

    // Fetch recently processed subscriptions (within last 5 minutes)
    useEffect(() => {
        const fetchProcessingSubscriptions = async () => {
            if (!open || !serviceProviderId) return;
            
            try {
                // Calculate timestamp for 5 minutes ago
                const fiveMinutesAgo = Timestamp.fromDate(
                    new Date(Date.now() - 5 * 60 * 1000)
                );
                
                // Query ActionLogs for recent bulk invoice actions
                const actionLogsRef = collection(db, `ServiceProviders/${serviceProviderId}/ActionLogs`);
                const q = query(
                    actionLogsRef,
                    where('action', '==', ActionType.raiseInvoiceBulk),
                    where('timestamp', '>=', fiveMinutesAgo)
                );
                
                const querySnapshot = await getDocs(q);
                const processingSet = new Set<string>();
                
                // Collect all subscription IDs from recent bulk invoice actions
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.selectedSubscriptions && Array.isArray(data.selectedSubscriptions)) {
                        data.selectedSubscriptions.forEach((subId: string) => {
                            processingSet.add(subId);
                        });
                    }
                });
                
                setProcessingSubscriptions(processingSet);
            } catch (error) {
                console.error('Error fetching processing subscriptions:', error);
            }
        };
        
        fetchProcessingSubscriptions();
    }, [open, serviceProviderId]);

    // UseEffect to initialize available cycles
    useEffect(() => {
        setIsSubscriptionsLoading(true);
        // Get future cycles for dropdown
        const cycles = getFutureCycles().slice(0, 2);
        setAvailableCycles(cycles);
        // Get all eligible subscription IDs
        let eligibleSubscriptionIds = subscriptions?.filter(subscription => subscription.showMeInList === true)
            .map(subscription => subscription.id) || [];

        eligibleSubscriptionIds = eligibleSubscriptionIds.filter((id: string) => !processingSubscriptions.has(id));

        // Initialize form with first cycle and all eligible subscriptions
        setFormData({
            cycle: cycles.length > 0 ? cycles[0] : "",
            selectedSubscriptions: eligibleSubscriptionIds
        });

        setIsSubscriptionsLoading(false);
    }, [open, subscriptions, processingSubscriptions]);


    // Handler Functions
    const handleClose = () => {
        dispatch(closeModal("BulkRaiseInvoiceModalOpen"));
        resetForm();
    };

    const handleSubscriptionChange = (subscriptionId: string, checked: boolean) => {
        setFormData(prev => {
            const newSubscriptions = checked
                ? [...prev.selectedSubscriptions, subscriptionId]
                : prev.selectedSubscriptions.filter(id => id !== subscriptionId);

            setFormData(prev => ({ ...prev, selectedSubscriptions: newSubscriptions }));

            return {
                ...prev,
                selectedSubscriptions: newSubscriptions
            };
        });
    };
    // Create an array of subscriptions with references and check eligibility
    const subscriptionsWithReference = subscriptions
        ? subscriptions.filter(subscription => subscription.showMeInList === true)
            .map(subscription => ({
                ...subscription,
                reference: doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscription.id}`),
                isEligible: true
            })) : [];

    // Reset Form Function
    const resetForm = () => {
        setFormData({
            cycle: availableCycles.length > 0 ? availableCycles[0] : "",
            selectedSubscriptions: []
        });
    };

    // Validation Function
    const validateForm = (): boolean => {
        if (formData.cycle === "") {
            toast.error("Please select an invoice cycle");
            return false;
        }

        if (formData.selectedSubscriptions.length === 0) {
            toast.error("Please select at least one subscription");
            return false;
        }

        return true;
    };

    // Submit Function
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        dispatch(toggleLoading(true));

        try {
            const requestData = {
                ProviderID: serviceProviderId,
                SubscriptionIDs: formData.selectedSubscriptions,
                cycle: formData.cycle,
                secretToken: secretCloudTaskToken
            };

            logAction({
                action: ActionType.raiseInvoiceBulk,
                timestamp: Timestamp.now(),
                platform: "web",
                performedBy: {
                    spId: spId || "",
                    name: spName || ""
                },
                selectedSubscriptions: formData.selectedSubscriptions,
                context: {
                    cycle: formData.cycle,
                    
                },
            }, serviceProviderId)

            const response: any = await axiosPostRequest(
                `${SECRETS_ENV.API_END_POINT}/api/BulkUsabilityFeatures/invoice`,
                requestData
            );

            if (response.data.message === "Invoice processing completed" && response.data
            ) {

                setInvoiceResults(response.data);
                setShowResults(true);
                toast.success("Invoices have been processed!");
                handleClose();
            } else {
                toast.error("Failed to raise invoices");
            }
            
        } catch (error) {
            console.error("Error raising invoices:", error);
            toast.error("Failed to raise invoices");
        } finally {
            setIsLoading(false);
            dispatch(toggleLoading(false));
        }
    };

    // Add these handler functions after the existing handler functions
    const handleSelectAll = () => {
        // Only select subscriptions that are not currently being processed
        const allSubscriptionIds = subscriptionsWithReference
            .filter((sub: { id: string; }) => !processingSubscriptions.has(sub.id))
            .map((sub: { id: any; }) => sub.id);
        setFormData(prev => ({
            ...prev,
            selectedSubscriptions: allSubscriptionIds
        }));
    };

    const handleDeselectAll = () => {
        setFormData(prev => ({
            ...prev,
            selectedSubscriptions: []
        }));
    };

    return (
        <>
            <Dialog
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '10px',
                    },
                }}
                maxWidth="md"
                open={open}
            >
                <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className="bg-white">
                    <div className="w-[550px]">
                        <div className="header pb-[20px]">
                            <p className="text-cf_heading font-[600]">Bulk Raise Invoice</p>
                            <p className="text-cf_small font-[400]">Raise invoices for multiple subscriptions at once</p>
                        </div>

                        <div className="absolute right-4 top-4">
                            <IconButton onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </div>

                        <Box className="space-y-4">

                            <TextField
                                size={"small"}
                                id="cycle-select"
                                select
                                fullWidth
                                label="Invoice Cycle"
                                defaultValue={0}
                                value={formData.cycle}
                                onChange={(e) => setFormData(prev => ({ ...prev, cycle: e.target.value }))}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'grey', borderRadius: '10px'// Default border color
                                        }, '&:hover fieldset': {
                                            borderColor: 'grey', // Border color on hover
                                        }, '&.Mui-focused fieldset': {
                                            borderColor: 'skyblue', // Border color when focused
                                        }, '& .MuiInputBase-input': {
                                            fontSize: '14px', // Setting font size
                                        },
                                    }, fontWeight: 400, fontSize: "1px",
                                }}
                            >
                                {availableCycles.map((cycle, index) => (
                                    <MenuItem key={index} value={cycle}>{cycle}</MenuItem>
                                ))}

                            </TextField>

                            <div className="mt-4">
                                <div className="flex justify-between items-center mb-2">
                                    <p className="text-sm font-medium">
                                        <span className="font-semibold">Select Subscriptions</span>
                                        <span className="font-[400]"> - ({formData.selectedSubscriptions.length} selected)</span>
                                        {processingSubscriptions.size > 0 && (
                                            <span className="ml-2 text-xs text-orange-600">
                                                ({processingSubscriptions.size} processing)
                                            </span>
                                        )}
                                    </p>
                                    <button
                                        onClick={
                                            formData.selectedSubscriptions.length === 
                                            subscriptionsWithReference.filter((sub: { id: string; }) => !processingSubscriptions.has(sub.id)).length 
                                                ? handleDeselectAll 
                                                : handleSelectAll
                                        }
                                        className="text-sm text-blue-600 hover:text-blue-800"
                                    >
                                        {formData.selectedSubscriptions.length === 
                                         subscriptionsWithReference.filter((sub: { id: string; }) => !processingSubscriptions.has(sub.id)).length 
                                            ? 'Deselect All' 
                                            : 'Select All'}
                                    </button>
                                </div>
                                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-2">
                                    {isSubscriptionsLoading ? (
                                        <div className="flex justify-center items-center h-20">
                                            <CircularProgress size={24} />
                                            Loading...
                                        </div>
                                    ) : (
                                        subscriptionsWithReference.map((subscription: { id: string; Name: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; activeSubscribersCount: any; subscriptionType: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; }, index: Key | null | undefined) => {
                                            const isProcessing = processingSubscriptions.has(subscription.id);
                                            
                                            return (
                                                <div
                                                    key={index}
                                                    className={`p-2 border-b last:border-b-0 ${isProcessing ? 'bg-gray-50 opacity-60' : ''}`}
                                                >
                                                    <Tooltip 
                                                        title={isProcessing ? "Raise invoice for this subscription is currently in process" : ""}
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <span>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={formData.selectedSubscriptions.some(
                                                                            id => id === subscription.id
                                                                        )}
                                                                        onChange={(e) => {
                                                                            if (isProcessing) {
                                                                                toast.info("Raise invoice for this subscription is currently in process");
                                                                                return;
                                                                            }
                                                                            handleSubscriptionChange(
                                                                                subscription.id,
                                                                                e.target.checked
                                                                            );
                                                                        }}
                                                                        disabled={isProcessing}
                                                                    />
                                                                }
                                                                label={
                                                                    <div className="flex flex-col">
                                                                        <span className={`font-medium ${isProcessing ? 'text-gray-500' : ''}`}>
                                                                            {subscription.Name}
                                                                            {isProcessing && (
                                                                                <span className="ml-2 text-xs text-orange-600 font-normal">
                                                                                    (Processing...)
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                        <span className="text-xs text-gray-500">
                                                                            {`${subscription.activeSubscribersCount} Students`} • {subscription.subscriptionType}
                                                                        </span>
                                                                    </div>
                                                                }
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            <div className="mt-2 bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                                <p className="font-medium">Note:</p>
                                <p>Paused/terminated students won't be raised invoice. All eligible students will be raised invoice.</p>
                            </div>

                            <div className="flex gap-2 pt-4">
                                <button
                                    disabled={isLoading}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleClose();
                                    }}
                                    className="w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg"
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                    {isLoading && <CircularProgress sx={{ color: "white" }} size={20} />}
                                    <p className="ml-2">{isLoading ? "Processing" : "Confirm"}</p>
                                </button>
                            </div>
                        </Box>
                    </div>
                </DialogContent>
            </Dialog>

            {invoiceResults && (
                <InvoiceResultsDrawer
                    open={showResults}
                    onClose={() => setShowResults(false)}
                    results={invoiceResults}
                />
            )}
        </>
    );
} 